import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import Button from "@atlaskit/button";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import SearchIcon from "@atlaskit/icon/glyph/search";
import Lozenge from "@atlaskit/lozenge";
import { media } from "@atlaskit/primitives/responsive";
import Textfield from "@atlaskit/textfield";

import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics.ts";

import {
    TemplateSearchItem,
    useTemplateFuzzySearchContext
} from "./context/template-fuzzy-search-context";

const searchBarContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    paddingBlockStart: "34px"
});

const searchBar = css({
    border: "1px solid #6B77BC",
    height: "57px",
    borderRadius: "7px",
    backgroundColor: "#FFFFFF",
    // Tech debt, should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    input: {
        justifyContent: "flex-start",
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "24px",
        color: "#000000",
        fontFamily: "var(--bxp-charlie-text-font-family)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        [media.below.sm]: {
            fontSize: "0.875rem"
        }
    },
    maxWidth: "965px",
    width: "100%"
});

const suggestionContainerStyle = css({
    backgroundColor: "#FFFFFF",
    border: "1px solid #6B778C",
    filter: "drop-shadow(0px 3px 10px rgba(0, 0, 0, 0.25))",
    textAlign: "left",
    alignSelf: "stretch",
    maxHeight: "17.75rem",
    overflowY: "auto",
    position: "relative",
    zIndex: "2",
    borderRadius: "7px"
});

const suggestionListStyle = css({
    "display": "flex",
    "alignItems": "center",
    "cursor": "pointer",
    "&:hover": {
        background: "#EAEAEA99"
    },
    "&[aria-selected=true]": {
        background: "#EAEAEA99"
    },
    "&:focus": {
        background: "#EAEAEA99"
    },
    "&:active": {
        background: "#EAEAEA99"
    },
    [media.below.sm]: {
        paddingInlineStart: "10px",
        paddingInlineEnd: "10px",
        justifyContent: "space-between"
    }
});

const suggestionItemStyle = css({
    justifyContent: "flex-start",
    fontSize: "1rem",
    fontWeight: "500",
    lineHeight: "24px",
    color: "#000000",
    fontFamily: "var(--bxp-charlie-text-font-family)",
    padding: "8px 6px",
    [media.below.sm]: {
        fontSize: "0.875rem"
    }
});

const searchIconStyle = css({
    height: "54px",
    width: "55px",
    color: "#808080",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});
const searchIconResultsStyle = css({
    [media.below.sm]: {
        display: "none"
    }
});

const closeButtonStyles = css({
    color: "#808080",
    border: "none",
    flexShrink: 0,
    cursor: "pointer"
});

const closeIconSectionStyles = css({
    "display": "flex",
    "justifyContent": "flex-end",
    "padding": "0",
    "border": "none",
    "alignItems": "center",
    "marginBlockStart": "8px",
    // Tech debt, should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& button": {
        "background": "none",
        "&:hover": {
            background: "none"
        },
        "&:active": {
            background: "none"
        }
    }
});

export const TemplateFuzzySearch = () => {
    const intl = useIntl();
    const { setSearchTerm, matchingSearchItems, searchSubmit, searchTerm } =
        useTemplateFuzzySearchContext();

    const [inputValue, setInputValue] = useState<string>("");
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [showItems, setShowItems] = useState(false);

    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const focusRef = useRef<HTMLDivElement>(null);
    const listItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const hasSentAnalytic = useRef(false);

    useEffect(() => {
        setInputValue(searchTerm ?? "");
    }, [searchTerm]);

    useEffect(() => {
        //Handling clicking outside the suggestion list, so it automatically closes when not the focus
        const handleClickOutside: EventListener = (event: Event) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target as Node) &&
                showItems
            ) {
                setShowItems(!showItems);
                setCurrentIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showItems]);

    const escFunction = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape" && showItems) {
                setShowItems(false);
            }
        },
        [showItems]
    );

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    // eslint-disable-next-line complexity
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown") {
            if (focusRef.current) {
                focusRef.current.focus();
            }
            if (currentIndex < matchingSearchItems.length - 1) {
                const newIndex = currentIndex + 1;
                setCurrentIndex(newIndex);

                //This is for the scrollbar and the user to be able to arrow up/down in the suggestion list
                listItemRefs.current[newIndex]?.scrollIntoView({
                    block: "nearest",
                    inline: "nearest"
                });
            }
        } else if (event.key === "ArrowUp") {
            //Prevent the default behavior of the arrow up key to prevent the cursor from moving to the beginning of the input field
            event.preventDefault();

            if (focusRef.current) {
                focusRef.current.focus();
            }
            if (currentIndex > 0) {
                const newIndex = currentIndex - 1;
                setCurrentIndex(newIndex);

                //This is for the scrollbar and the user to be able to arrow up/down in the suggestion list
                listItemRefs.current[newIndex]?.scrollIntoView({
                    block: "nearest",
                    inline: "nearest"
                });
            }
        } else if (event.key === "Enter") {
            //Since the onKeyDownButton event is not working, we are setting all the values here - ideally this should be elsewhere
            if (matchingSearchItems[currentIndex]?.name) {
                setInputValue(
                    matchingSearchItems[currentIndex]?.name ?? inputValue
                );
                setSearchTerm(
                    matchingSearchItems[currentIndex]?.name ?? inputValue
                );
                searchSubmit({ searchItem: matchingSearchItems[currentIndex] });
            } else {
                setInputValue(
                    matchingSearchItems[currentIndex]?.name ?? inputValue
                );
                setSearchTerm(
                    matchingSearchItems[currentIndex]?.name ?? inputValue
                );
                searchSubmit({ searchTerm: inputValue });
            }
            setShowItems(false);
        }
    };

    const inputPlaceholder = intl.formatMessage(
        ConfluenceTemplateMessages.fuzzySearchInputText
    );

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();

        // Send analytics data only the first time
        if (!hasSentAnalytic.current) {
            ConfluenceTemplateAnalytics.sendTemplateFuzzySearchAnalytic();
            hasSentAnalytic.current = true; // Set the flag to true after the first call
        }
        const value = event?.target?.value;
        setInputValue(value);
        setSearchTerm(value);

        if (value) {
            setShowItems(true);
        } else {
            setShowItems(false);
            setCurrentIndex(-1);
        }
    };

    const handleClose = () => {
        setSearchTerm("");
        setInputValue("");
        setCurrentIndex(-1);
        setShowItems(false);
    };

    const handleSearchItemClick = (searchItem: TemplateSearchItem) => {
        if (searchItem) {
            setInputValue(searchItem?.name ?? "");
            setSearchTerm(searchItem?.name ?? "");
            searchSubmit({ searchItem });
            setShowItems(false);
            ConfluenceTemplateAnalytics.sendTemplateFuzzySearchPreviewAnalytic(
                searchItem
            );
        }
    };

    const onFocus = () => {
        ConfluenceTemplateAnalytics.sendTemplateFuzzySearchFocusAnalytic();
    };

    return (
        <div css={searchBarContainer}>
            <div ref={searchRef} css={searchBar}>
                <Textfield
                    ref={inputRef}
                    id="templates-fuzzy-search-input"
                    testId="templates-fuzzy-search-input"
                    placeholder={inputPlaceholder}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={onFocus}
                    onClick={() => setShowItems(true)}
                    value={inputValue}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-owns="suggestion-box"
                    aria-expanded={matchingSearchItems.length > 0}
                    aria-activedescendant={
                        currentIndex === -1
                            ? undefined
                            : "option-" + currentIndex
                    }
                    elemBeforeInput={
                        <div css={searchIconStyle}>
                            <SearchIcon
                                label={intl.formatMessage(
                                    ConfluenceTemplateMessages.searchLabel
                                )}
                                size="medium"
                            />
                        </div>
                    }
                    elemAfterInput={
                        inputValue ? (
                            <div css={closeIconSectionStyles}>
                                <Button
                                    id="clear-button"
                                    type="submit"
                                    color="#fff"
                                    css={closeButtonStyles}
                                    onClick={handleClose}
                                    testId="clear-button"
                                >
                                    <CrossIcon
                                        label={intl.formatMessage(
                                            ConfluenceTemplateMessages.closeLabel
                                        )}
                                        primaryColor={"#808080"}
                                        size="medium"
                                    />
                                </Button>
                            </div>
                        ) : (
                            ""
                        )
                    }
                />
                {matchingSearchItems && showItems && (
                    <div
                        ref={focusRef}
                        css={suggestionContainerStyle}
                        id="suggestion-box"
                        role="listbox"
                        aria-labelledby={"option-" + currentIndex}
                    >
                        {matchingSearchItems.map((searchItem, idx) => (
                            // We do not need a keyboard event handler on this element since a keyboard event gets handled by the textfield onkeydown handler
                            // This element does not need to support focus. The active search result is identified by aria-activedescendant.
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
                            <div
                                id={"option-" + idx}
                                key={searchItem.name + "-" + idx}
                                ref={(node) => {
                                    if (node) {
                                        if (listItemRefs?.current) {
                                            listItemRefs.current[idx] = node;
                                        }
                                    } else {
                                        if (listItemRefs?.current) {
                                            listItemRefs.current[idx] = null;
                                        }
                                    }
                                }}
                                css={suggestionListStyle}
                                role="option"
                                aria-selected={currentIndex === idx}
                                onClick={() =>
                                    handleSearchItemClick(searchItem)
                                }
                                aria-label={`Select ${searchItem.name}`}
                            >
                                <div
                                    css={[
                                        searchIconStyle,
                                        searchIconResultsStyle
                                    ]}
                                >
                                    <SearchIcon
                                        label={intl.formatMessage(
                                            ConfluenceTemplateMessages.searchLabel
                                        )}
                                        size="medium"
                                    />
                                </div>
                                <div css={suggestionItemStyle}>
                                    {searchItem.name}
                                </div>
                                <span>
                                    {searchItem.type === "category" && (
                                        <Lozenge appearance="success">
                                            <FormattedMessage
                                                id="confluence.main.area.cfind.fuzzysearch.label.category"
                                                defaultMessage="CATEGORY"
                                            />
                                        </Lozenge>
                                    )}
                                    {searchItem.type === "template" && (
                                        <Lozenge appearance="inprogress">
                                            <FormattedMessage
                                                id="confluence.main.area.cfind.fuzzysearch.label.template"
                                                defaultMessage="TEMPLATE"
                                            />
                                        </Lozenge>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
